import React, { useState, createContext, useContext, useRef } from 'react';
import api from '../services/api';
import { normalizarComanda, mesclarComandas, formatDataKeyCuiaba } from '../utils/dateUtils';

const DataContext = createContext(null);

const CACHE_TTL = 30000;

// Sem parâmetros a API devolve só as 500 comandas mais recentes da barbearia (~3 semanas).
// A carga inicial vai do mês anterior até hoje; períodos mais antigos vêm por carregarHistorico.
const LIMITE_COMANDAS = 50000;
const PENDENTES_DESDE = '2025-11-10';
const TENTATIVAS_CARGA = 3;

const janelaInicialComandas = () => {
  const agora = new Date();
  const [ano, mes] = formatDataKeyCuiaba(agora).split('-').map(Number);
  const anoAnterior = mes === 1 ? ano - 1 : ano;
  const mesAnterior = mes === 1 ? 12 : mes - 1;
  return {
    dataInicio: `${anoAnterior}-${String(mesAnterior).padStart(2, '0')}-01`,
    dataFim: formatDataKeyCuiaba(new Date(agora.getTime() + 86400000)),
    pendentesDesde: PENDENTES_DESDE,
    limit: LIMITE_COMANDAS,
  };
};

export const DataProvider = ({ children, usuariosState }) => {
  const [servicos, setServicos] = useState([]);
  const [barbeiros, setBarbeiros] = useState([]);
  const [usuarios, setUsuarios] = usuariosState;
  const [comandas, setComandas] = useState([]);
  const [agendamentos, setAgendamentos] = useState([]);
  const [configuracoes, setConfiguracoes] = useState({ comissaoPadrao: 48 });
  const [disponibilidade, setDisponibilidade] = useState({});
  const lastFetchRef = useRef(0);
  const fetchingRef = useRef(false);
  const comandasDesdeRef = useRef(null);
  const tentativasRef = useRef(0);
  const retryRef = useRef(null);

  // Ao sair, nada do usuário anterior pode sobrar para o próximo login
  const limparDados = React.useCallback(() => {
    clearTimeout(retryRef.current);
    lastFetchRef.current = 0;
    tentativasRef.current = 0;
    comandasDesdeRef.current = null;
    setServicos([]); setBarbeiros([]); setUsuarios([]); setComandas([]); setAgendamentos([]);
  }, []);

  const carregarDados = React.useCallback(async (force = false) => {
    if (!force && Date.now() - lastFetchRef.current < CACHE_TTL) return;
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      const janela = janelaInicialComandas();
      // Uma chamada que falha não pode esvaziar o que as outras trouxeram
      const [servicosRes, usuariosRes, barbeirosRes, comandasRes, configsRes] = await Promise.allSettled([
        api.servicos.getAll(),
        api.users.getAll(),
        api.barbeiros.getAll(),
        api.comandas.getAll(janela),
        api.configuracoes.get(),
      ]);
      if (servicosRes.status === 'fulfilled' && Array.isArray(servicosRes.value)) {
        setServicos(servicosRes.value.map(s => ({ ...s, preco: parseFloat(s.preco || 0), ativo: s.ativo !== false })));
      }
      if (usuariosRes.status === 'fulfilled') setUsuarios(usuariosRes.value);
      if (barbeirosRes.status === 'fulfilled') setBarbeiros(barbeirosRes.value);
      if (comandasRes.status === 'fulfilled') {
        comandasDesdeRef.current = janela.dataInicio;
        setComandas(comandasRes.value.map(c => normalizarComanda(c)));
      }
      if (configsRes.status === 'fulfilled') {
        const padrao = parseFloat(configsRes.value.comissaoPadrao);
        setConfiguracoes({ comissaoPadrao: Number.isFinite(padrao) ? padrao : 48 });
      }

      const falhas = [servicosRes, usuariosRes, barbeirosRes, comandasRes, configsRes].filter(r => r.status === 'rejected');
      const sessaoInvalida = falhas.some(r => r.reason?.isAuthError || r.reason?.response?.status === 401);
      if (falhas.length === 0) {
        lastFetchRef.current = Date.now();
        tentativasRef.current = 0;
      } else if (!sessaoInvalida && tentativasRef.current < TENTATIVAS_CARGA) {
        tentativasRef.current += 1;
        console.error('Erro ao carregar dados:', falhas[0].reason);
        retryRef.current = setTimeout(() => carregarDados(true), 5000);
      }
    } finally {
      fetchingRef.current = false;
    }
  }, []);

  // Garante as comandas a partir de dataInicio (YYYY-MM-DD) para períodos anteriores à carga inicial
  const carregarHistorico = React.useCallback(async (dataInicio) => {
    if (!/^20\d{2}-\d{2}-\d{2}$/.test(dataInicio || '')) return;
    const desde = comandasDesdeRef.current;
    if (!desde || dataInicio >= desde) return;
    comandasDesdeRef.current = dataInicio;
    try {
      const antigas = await api.comandas.getAll({ dataInicio, dataFim: desde, limit: LIMITE_COMANDAS });
      setComandas((prev) => mesclarComandas(prev, antigas.map((c) => normalizarComanda(c))));
    } catch (error) {
      comandasDesdeRef.current = desde;
      if (error.isAuthError || error.response?.status === 401) return;
      console.error('Erro ao carregar histórico de comandas:', error);
    }
  }, []);

  return (
    <DataContext.Provider value={{
      servicos, setServicos, barbeiros, setBarbeiros,
      comandas, setComandas, agendamentos, setAgendamentos,
      configuracoes, setConfiguracoes, disponibilidade, setDisponibilidade,
      usuarios, setUsuarios, carregarDados, carregarHistorico, limparDados
    }}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => useContext(DataContext);
